// Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import React, { useState } from 'react';
import {
    Box, Typography, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Stack,
    Divider, Chip, Collapse, Tooltip
} from '@mui/material';
import { useAuthContext } from "@asgardeo/auth-react";
import { Store } from 'react-notifications-component';
import { ProjectDetail, ProjectHealthStatus, ProjectRisk } from '../data/utils/types';
import { RESOURCE_SERVER_URLS } from '../data/configs/spl-config';
import { useGetApi } from '../data/hooks/api';
import CircularLoading from './Loading';

interface ReviewStatusCellProps {
    project: ProjectDetail;
    accountSysId: string;
    healthStatus: ProjectHealthStatus | null;
    onStatusChanged: () => void;
    onAddActionItem?: () => void;
}

function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });
}

const ReviewStatusCell: React.FC<ReviewStatusCellProps> = ({
    project, accountSysId, healthStatus, onStatusChanged, onAddActionItem
}) => {
    const { httpRequest, getAccessToken } = useAuthContext();
    const [loading, setLoading] = useState(false);

    const [markAtRiskOpen, setMarkAtRiskOpen] = useState(false);
    const [closeRiskOpen, setCloseRiskOpen] = useState(false);
    const [markHealthyOpen, setMarkHealthyOpen] = useState(false);
    const [postCloseOpen, setPostCloseOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);

    const [atRiskComment, setAtRiskComment] = useState('');
    const [markHealthyComment, setMarkHealthyComment] = useState('');
    const [closeRiskComment, setCloseRiskComment] = useState('');
    const [expandedActionItems, setExpandedActionItems] = useState<Set<number>>(new Set());

    const { data: historyData, loading: historyLoading, getApiData: fetchHistory } = useGetApi<ProjectRisk[]>({
        url: RESOURCE_SERVER_URLS[0] + `/customer-health/projects/${project.sysId}/risk-history`
    });

    const status = healthStatus?.healthStatus?.status ?? 'to_be_reviewed';
    const openRisk = healthStatus?.openRisk ?? null;
    const hasOpenItems = (openRisk?.actionItems ?? []).some(
        item => item.status === 'open' || item.status === 'in_progress'
    );

    const handleMarkAtRisk = async () => {
        if (!atRiskComment.trim()) return;
        setLoading(true);
        try {
            const token = await getAccessToken();
            await httpRequest({
                url: RESOURCE_SERVER_URLS[0] + `/customer-health/projects/${project.sysId}/risk`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'x-jwt-assertion': token },
                data: { accountSysId, comment: atRiskComment }
            });
            Store.addNotification({
                title: "Success!",
                message: `${project.name} marked as at risk.`,
                type: "success",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 5000, onScreen: true }
            });
            setMarkAtRiskOpen(false);
            setAtRiskComment('');
            onStatusChanged();
        } catch (error: any) {
            Store.addNotification({
                title: "Failed!",
                message: "Failed to mark project as at risk.",
                type: "danger",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 5000, onScreen: true }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleMarkHealthy = async () => {
        if (!markHealthyComment.trim()) return;
        setLoading(true);
        try {
            const token = await getAccessToken();
            await httpRequest({
                url: RESOURCE_SERVER_URLS[0] + `/customer-health/projects/${project.sysId}/mark-healthy`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'x-jwt-assertion': token },
                data: { accountSysId, comment: markHealthyComment.trim() }
            });
            Store.addNotification({
                title: "Success!",
                message: `${project.name} marked as healthy.`,
                type: "success",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 5000, onScreen: true }
            });
            setMarkHealthyOpen(false);
            setMarkHealthyComment('');
            onStatusChanged();
        } catch (error: any) {
            Store.addNotification({
                title: "Failed!",
                message: "Failed to mark project as healthy.",
                type: "danger",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 5000, onScreen: true }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseRisk = async () => {
        if (!closeRiskComment.trim()) return;
        setLoading(true);
        try {
            const token = await getAccessToken();
            await httpRequest({
                url: RESOURCE_SERVER_URLS[0] + `/customer-health/risks/${openRisk!.id}/close`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'x-jwt-assertion': token },
                data: { comment: closeRiskComment }
            });
            Store.addNotification({
                title: "Success!",
                message: `Risk closed for ${project.name}.`,
                type: "success",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 5000, onScreen: true }
            });
            setCloseRiskOpen(false);
            setCloseRiskComment('');
            setPostCloseOpen(true);
        } catch (error: any) {
            const message = error?.response?.data?.message ?? "Failed to close risk.";
            Store.addNotification({
                title: "Failed!",
                message,
                type: "danger",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: { duration: 5000, onScreen: true }
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePostCloseLeave = () => {
        setPostCloseOpen(false);
        onStatusChanged();
    };

    const handleOpenHistory = () => {
        fetchHistory();
        setHistoryOpen(true);
    };

    const riskCycleCount = historyData ? historyData.filter(r => r.openedComment !== 'Marked as healthy').length : 0;
    const healthyReviewCount = historyData ? historyData.filter(r => r.openedComment === 'Marked as healthy').length : 0;

    // Assign risk numbers oldest→#1, newest→#N (sorted by openedOn ascending)
    const riskNumberMap = new Map<number, number>();
    if (historyData) {
        const sortedAsc = [...historyData]
            .filter(r => r.openedComment !== 'Marked as healthy')
            .sort((a, b) => new Date(a.openedOn).getTime() - new Date(b.openedOn).getTime());
        sortedAsc.forEach((r, idx) => riskNumberMap.set(r.id, idx + 1));
    }

    // Display order: open risk first, then rest by date descending
    const displayHistory = historyData
        ? [...historyData].sort((a, b) => {
            const aIsOpen = a.status === 'open' && a.openedComment !== 'Marked as healthy';
            const bIsOpen = b.status === 'open' && b.openedComment !== 'Marked as healthy';
            if (aIsOpen && !bIsOpen) return -1;
            if (!aIsOpen && bIsOpen) return 1;
            return new Date(b.openedOn).getTime() - new Date(a.openedOn).getTime();
        })
        : [];

    return (
        <Box>
            <CircularLoading openLoading={loading} />
            <Box>
                {status === 'to_be_reviewed' && (
                    <>
                        <Typography variant="caption" color="text.secondary">To Be Reviewed</Typography>
                        <Box sx={{ mt: 2, maxWidth: 200, mx: 'auto' }}>
                            <Stack spacing={1}>
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="contained"
                                    sx={{ backgroundColor: '#EF5350', '&:hover': { backgroundColor: '#D32F2F' }, color: '#fff', fontWeight: 600, textTransform: 'none', py: 0.75 }}
                                    onClick={() => setMarkAtRiskOpen(true)}
                                >
                                    Mark At Risk
                                </Button>
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="contained"
                                    sx={{ backgroundColor: '#66BB6A', '&:hover': { backgroundColor: '#43A047' }, color: '#fff', fontWeight: 600, textTransform: 'none', py: 0.75 }}
                                    onClick={() => setMarkHealthyOpen(true)}
                                >
                                    Mark Healthy
                                </Button>
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleOpenHistory}
                                    sx={{ textTransform: 'none', borderColor: '#e96900', color: '#e96900', '&:hover': { backgroundColor: '#fff3e0', borderColor: '#e96900' } }}
                                >
                                    View Health History
                                </Button>
                            </Stack>
                        </Box>
                    </>
                )}
                {status === 'at_risk' && (
                    <>
                        <Chip label="At Risk" size="small" sx={{ fontWeight: 600, borderRadius: '16px', backgroundColor: 'transparent', border: '2px solid #C62828', color: '#C62828', boxShadow: '0 0 8px rgba(198, 40, 40, 0.3)', '&:hover': { backgroundColor: 'transparent' } }} />
                        {openRisk && (
                            <>
                                <Box sx={{ mt: 2, p: 1.5, borderLeft: '3px solid #D32F2F', backgroundColor: '#fafafa', borderRadius: '0 4px 4px 0', overflow: 'hidden', minWidth: 0 }}>
                                    <Typography variant="body2" sx={{
                                        color: '#b71c1c',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        maxWidth: '100%',
                                        lineHeight: 1.5,
                                    }}>
                                        {openRisk.openedComment}
                                    </Typography>
                                    <Button
                                        size="small" variant="text"
                                        sx={{ p: 0, minWidth: 0, fontSize: '0.7rem', color: '#b71c1c', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
                                        onClick={handleOpenHistory}
                                    >
                                        Show more
                                    </Button>
                                    <Typography variant="caption" display="block" mt={0.75} color="text.secondary">
                                        {openRisk.openedByEmail} · {formatDate(openRisk.openedOn)}
                                    </Typography>
                                </Box>

                                <Divider sx={{ mt: 2, mb: 1 }} />

                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                    {(openRisk.actionItems ?? []).filter(i => i.status === 'open').length} open ·{' '}
                                    {(openRisk.actionItems ?? []).filter(i => i.status === 'in_progress').length} in progress
                                </Typography>

                                <Box sx={{ maxWidth: 200, mx: 'auto' }}>
                                    <Stack spacing={1}>
                                        {onAddActionItem && (
                                            <Button
                                                size="small"
                                                fullWidth
                                                variant="outlined"
                                                sx={{ textTransform: 'none', color: '#e96900', borderColor: '#e96900', '&:hover': { backgroundColor: '#fff3e0', borderColor: '#e96900' } }}
                                                onClick={onAddActionItem}
                                            >
                                                + Add Action Item
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            fullWidth
                                            variant="outlined"
                                            sx={{ textTransform: 'none', color: '#757575', borderColor: '#bdbdbd', '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#9e9e9e' } }}
                                            onClick={() => document.getElementById('action-items-section')?.scrollIntoView({ behavior: 'smooth' })}
                                        >
                                            View All Action Items
                                        </Button>
                                        <Tooltip
                                            title={hasOpenItems ? "Complete all open and in-progress action items for this project before closing the risk." : ""}
                                            arrow
                                        >
                                            <span style={{ display: 'block' }}>
                                                <Button
                                                    size="small"
                                                    fullWidth
                                                    variant="outlined"
                                                    color="error"
                                                    disabled={hasOpenItems}
                                                    onClick={() => setCloseRiskOpen(true)}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    Close Risk
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Button
                                            size="small"
                                            fullWidth
                                            variant="outlined"
                                            onClick={handleOpenHistory}
                                            sx={{ textTransform: 'none', borderColor: '#e96900', color: '#e96900', '&:hover': { backgroundColor: '#fff3e0', borderColor: '#e96900' } }}
                                        >
                                            View Health History
                                        </Button>
                                    </Stack>
                                </Box>
                            </>
                        )}
                    </>
                )}

                {status === 'healthy' && (
                    <>
                        <Chip label="Healthy" size="small" sx={{ fontWeight: 600, borderRadius: '16px', backgroundColor: 'transparent', border: '2px solid #2E7D32', color: '#2E7D32', boxShadow: '0 0 8px rgba(46, 125, 50, 0.3)', '&:hover': { backgroundColor: 'transparent' } }} />
                        {healthStatus?.healthStatus && (
                            <Typography variant="caption" display="block" mt={2} color="text.secondary">
                                {healthStatus.healthStatus.reviewedByEmail} · {formatDate(healthStatus.healthStatus.reviewedOn)}
                            </Typography>
                        )}
                        <Box sx={{ mt: 2, maxWidth: 200, mx: 'auto' }}>
                            <Stack spacing={1}>
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="contained"
                                    sx={{ backgroundColor: '#EF5350', '&:hover': { backgroundColor: '#D32F2F' }, color: '#fff', fontWeight: 600, textTransform: 'none', py: 0.75 }}
                                    onClick={() => setMarkAtRiskOpen(true)}
                                >
                                    Mark At Risk
                                </Button>
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleOpenHistory}
                                    sx={{ textTransform: 'none', borderColor: '#e96900', color: '#e96900', '&:hover': { backgroundColor: '#fff3e0', borderColor: '#e96900' } }}
                                >
                                    View Health History
                                </Button>
                            </Stack>
                        </Box>
                    </>
                )}
            </Box>

            {/* Mark At Risk Dialog */}
            <Dialog open={markAtRiskOpen} onClose={() => setMarkAtRiskOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Mark {project.name} as At Risk</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        required
                        multiline
                        rows={4}
                        placeholder="Add reason for marking at risk..."
                        value={atRiskComment}
                        onChange={e => setAtRiskComment(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setMarkAtRiskOpen(false); setAtRiskComment(''); }}>Cancel</Button>
                    <Button
                        onClick={handleMarkAtRisk}
                        disabled={!atRiskComment.trim()}
                        sx={{ color: '#e65100' }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mark Healthy Dialog */}
            <Dialog open={markHealthyOpen} onClose={() => { setMarkHealthyOpen(false); setMarkHealthyComment(''); }} maxWidth="sm" fullWidth>
                <DialogTitle>Mark {project.name} as Healthy</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        required
                        multiline
                        rows={4}
                        placeholder="Add reason for marking healthy..."
                        value={markHealthyComment}
                        onChange={e => setMarkHealthyComment(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setMarkHealthyOpen(false); setMarkHealthyComment(''); }}>Cancel</Button>
                    <Button onClick={handleMarkHealthy} disabled={!markHealthyComment.trim()} sx={{ color: '#2e7d32' }}>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Close Risk Dialog */}
            <Dialog open={closeRiskOpen} onClose={() => setCloseRiskOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Close Risk for {project.name}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        required
                        multiline
                        rows={4}
                        placeholder="Add reason for closing risk..."
                        value={closeRiskComment}
                        onChange={e => setCloseRiskComment(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCloseRiskOpen(false); setCloseRiskComment(''); }}>Cancel</Button>
                    <Button onClick={handleCloseRisk} disabled={!closeRiskComment.trim()}>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Post Close Follow-up Dialog */}
            <Dialog open={postCloseOpen} onClose={handlePostCloseLeave} maxWidth="sm" fullWidth>
                <DialogTitle>Mark Project as Healthy?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This project's risk has been closed. Would you like to mark it as Healthy, or leave it as To Be Reviewed?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePostCloseLeave}>Leave as To Be Reviewed</Button>
                    <Button onClick={() => { setPostCloseOpen(false); setMarkHealthyOpen(true); }} sx={{ color: '#2e7d32' }}>Mark Healthy</Button>
                </DialogActions>
            </Dialog>

            {/* Health Status History Dialog */}
            <Dialog open={historyOpen} onClose={() => { setHistoryOpen(false); setExpandedActionItems(new Set()); }} maxWidth="md" fullWidth>
                <DialogTitle>
                    Health Status History — {project.name}
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {riskCycleCount} risk cycle(s) · {healthyReviewCount} healthy review(s)
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {historyLoading && (
                        <Typography variant="body2">Loading...</Typography>
                    )}
                    {!historyLoading && (!historyData || historyData.length === 0) && (
                        <Typography variant="body2" color="text.secondary">No history for this project.</Typography>
                    )}
                    {!historyLoading && historyData && historyData.length > 0 && (
                        <Box sx={{ pt: 1 }}>
                            {displayHistory.map((risk, idx, arr) => {
                                const isHealthyRecord = risk.openedComment === 'Marked as healthy';
                                const isLast = idx === arr.length - 1;
                                const dotColor = isHealthyRecord ? '#2E7D32' : '#D32F2F';
                                return (
                                    <Box key={risk.id} sx={{ display: 'flex', mb: 2.5 }}>
                                        {/* Timeline dot + line */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2, flexShrink: 0, width: 20 }}>
                                            <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: dotColor, mt: '20px', flexShrink: 0 }} />
                                            {!isLast && <Box sx={{ width: 2, flex: 1, minHeight: 16, backgroundColor: '#e0e0e0', mt: 0.5 }} />}
                                        </Box>
                                        {/* Entry card */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {isHealthyRecord ? (
                                                <Box sx={{ border: '1px solid #c8e6c9', borderLeft: '3px solid #2E7D32', borderRadius: 1, backgroundColor: '#f1f8f1', p: 2 }}>
                                                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                                        <Chip label="Healthy" size="small" sx={{ bgcolor: '#2E7D32', color: '#fff', fontWeight: 700 }} />
                                                        <Typography variant="body2" color="text.secondary">{formatDate(risk.openedOn)}</Typography>
                                                    </Stack>
                                                    <Typography variant="body2">
                                                        Reviewed by: <strong style={{ color: '#1a237e' }}>{risk.openedByEmail}</strong>
                                                    </Typography>
                                                    {risk.closedComment && risk.closedComment !== 'Reviewed and confirmed healthy' && (
                                                        <Box sx={{ borderLeft: '3px solid #2E7D32', backgroundColor: '#e8f5e9', p: 1, borderRadius: '0 4px 4px 0', mt: 1 }}>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
                                                                {risk.closedComment}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Box sx={{ border: '1px solid #ffcdd2', borderLeft: '3px solid #D32F2F', borderRadius: 1, overflow: 'hidden' }}>
                                                    {/* Opened section */}
                                                    <Box sx={{ p: 2 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>
                                                            Risk #{riskNumberMap.get(risk.id)}
                                                        </Typography>
                                                        {risk.status === 'open' && (
                                                            <Box sx={{ mb: 0.75 }}>
                                                                <Chip label="Currently Open" size="small" sx={{
                                                                    bgcolor: '#D32F2F', color: '#fff', fontWeight: 700,
                                                                    animation: 'riskPulse 1.5s ease-in-out infinite',
                                                                    '@keyframes riskPulse': {
                                                                        '0%':   { boxShadow: '0 0 0 0 rgba(211,47,47,0.5)' },
                                                                        '70%':  { boxShadow: '0 0 0 7px rgba(211,47,47,0)' },
                                                                        '100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0)' },
                                                                    },
                                                                }} />
                                                            </Box>
                                                        )}
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            Opened on {formatDate(risk.openedOn)}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            By: <strong style={{ color: '#1a237e' }}>{risk.openedByEmail}</strong>
                                                        </Typography>
                                                        <Box sx={{ borderLeft: '3px solid #D32F2F', backgroundColor: '#fff3f3', p: 1, borderRadius: '0 4px 4px 0', mt: 1 }}>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
                                                                <strong>Reason:</strong> {risk.openedComment}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    {/* Closed section */}
                                                    {risk.closedOn && (
                                                        <>
                                                            <Divider />
                                                            <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                    Risk #{riskNumberMap.get(risk.id)} was closed on <strong>{formatDate(risk.closedOn)}</strong>
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    By: <strong style={{ color: '#1a237e' }}>{risk.closedByEmail}</strong>
                                                                </Typography>
                                                                {risk.closedComment && (
                                                                    <Box sx={{ borderLeft: '3px solid #9e9e9e', backgroundColor: '#eeeeee', p: 1, borderRadius: '0 4px 4px 0', mt: 1 }}>
                                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
                                                                            <strong>Resolution:</strong> {risk.closedComment}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </>
                                                    )}
                                                    {/* Action items section */}
                                                    {risk.actionItems.length > 0 && (
                                                        <>
                                                            <Divider />
                                                            <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="text"
                                                                    sx={{ p: 0, textTransform: 'none', fontWeight: 600, color: '#424242' }}
                                                                    onClick={() => {
                                                                        setExpandedActionItems(prev => {
                                                                            const next = new Set(prev);
                                                                            if (next.has(risk.id)) next.delete(risk.id);
                                                                            else next.add(risk.id);
                                                                            return next;
                                                                        });
                                                                    }}
                                                                >
                                                                    Action Items ({risk.actionItems.length}) {expandedActionItems.has(risk.id) ? '▴' : '▾'}
                                                                </Button>
                                                                <Collapse in={expandedActionItems.has(risk.id)}>
                                                                    <Box sx={{ mt: 1 }}>
                                                                        {risk.actionItems.map(item => (
                                                                            <Box key={item.id} sx={{ mb: 1 }}>
                                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                                    <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
                                                                                    <Chip
                                                                                        label={item.status}
                                                                                        size="small"
                                                                                        color={item.status === 'resolved' ? 'success' : 'default'}
                                                                                    />
                                                                                </Stack>
                                                                                {item.resolutionComment && (
                                                                                    <Typography variant="caption" display="block" color="text.secondary" mt={0.25}>
                                                                                        {item.resolutionComment}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                </Collapse>
                                                            </Box>
                                                        </>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setHistoryOpen(false); setExpandedActionItems(new Set()); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReviewStatusCell;
